import React, { useMemo, useState } from 'react';
import {
  Search,
  Loader2,
  Database,
  AlertTriangle,
  RefreshCw,
  Users,
  Building2,
} from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import {
  branchesOfBankZone,
  isPlaceholderName,
  zonesOfBank,
} from '../../utils/hierarchy';
import { userInBranch } from '../../utils/branchMatch';

/**
 * Choose first, read second.
 *
 * Opening Accounts used to read every account in the database — whether you
 * wanted one branch or all of them, and again for every other person who
 * signed in. Firestore bills reads, so at 80,000 accounts and ten users that
 * is roughly 800,000 reads a day to look at the same handful of records.
 *
 * Nothing is read until a bank, branch or agent is chosen here. The counts
 * shown beside each choice come from count queries, which are billed as ONE
 * read each however many records they count — so you can see how big a slice
 * is before deciding to open it.
 *
 * Only administrators and management see this. An agent's own allotment loads
 * automatically, because it is small and it is all they can see anyway.
 */

export const AccountsScopeBar: React.FC = () => {
  const {
    banks,
    zones,
    branches,
    users,
    accounts,
    accountsScope,
    accountsLoading,
    loadAccountsForScope,
    loadAllAccounts,
    countAccountsFor,
  } = useSRMS();

  const [bank, setBank] = useState('');
  const [zone, setZone] = useState('');
  const [branch, setBranch] = useState('');
  const [agentId, setAgentId] = useState('');
  const [message, setMessage] = useState('');
  const [counting, setCounting] = useState(false);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [expanded, setExpanded] = useState(false);

  const bankOptions = useMemo(
    () =>
      Array.from(
        new Set(banks.map((b) => (b.name || '').trim()).filter((n) => !isPlaceholderName(n)))
      ).sort(),
    [banks]
  );

  const zoneOptions = useMemo(
    () =>
      bank
        ? Array.from(
            new Set(
              zonesOfBank(zones, bank)
                .map((z) => (z.name || '').trim())
                .filter((n) => !isPlaceholderName(n))
            )
          ).sort()
        : [],
    [zones, bank]
  );

  const branchOptions = useMemo(
    () =>
      bank
        ? Array.from(
            new Set(
              branchesOfBankZone(branches, bank, zone || undefined)
                .map((b) => (b.name || '').trim())
                .filter((n) => !isPlaceholderName(n))
            )
          ).sort()
        : [],
    [branches, bank, zone]
  );

  /** Agents inside the chosen scope, so the list is not every agent ever. */
  const agentOptions = useMemo(() => {
    const live = users.filter((u) => u.role === 'agent' && u.active);
    if (!bank) return live;
    return live.filter((u) => {
      const theirBanks = u.banks && u.banks.length ? u.banks : [u.bank];
      const bankOk = theirBanks.some(
        (b) => (b || '').trim().toLowerCase() === bank.trim().toLowerCase()
      );
      if (!bankOk) return false;
      if (!branch) return true;
      // Branch id first, then name AND bank: two banks can both have a
      // branch called Bidkin. See src/utils/branchMatch.ts.
      return userInBranch(u, branch, bank, branches);
    });
  }, [users, bank, branch, branches]);

  const chosenAgent = users.find((u) => (u.agentId || u.id) === agentId);

  const canLoad = !!(agentId || branch || bank);

  const doLoad = async () => {
    if (!canLoad) return;
    setMessage('');
    const res = await loadAccountsForScope(
      agentId
        ? { agentId, agentName: chosenAgent?.name }
        : branch
        ? { branch }
        : { bank }
    );
    setMessage(res.message);
    if (res.success) setExpanded(false);
  };

  /**
   * Show how big each choice is before it is opened.
   *
   * One count query per row, each billed as a single read. Counting six
   * branches costs six reads; opening them to find out would cost thousands.
   */
  const doCount = async () => {
    setCounting(true);
    setMessage('');
    try {
      const next: Record<string, number> = {};
      if (agentId) {
        next[`agent:${agentId}`] = await countAccountsFor({ agentId });
      } else if (branch) {
        next[`branch:${branch}`] = await countAccountsFor({ branch });
      } else if (bank) {
        // Count the bank, then each of its branches, so you can see where the
        // records actually are. Capped at 12 so this never becomes expensive.
        next[`bank:${bank}`] = await countAccountsFor({ bank });
        for (const br of branchOptions.slice(0, 12)) {
          next[`branch:${br}`] = await countAccountsFor({ branch: br });
        }
      }
      setCounts((prev) => ({ ...prev, ...next }));
    } catch (err: any) {
      setMessage(`Could not count: ${err?.message || 'unknown error'}`);
    } finally {
      setCounting(false);
    }
  };

  const fieldCls =
    'w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-slate-100 disabled:text-slate-400';

  /* ---------------- already loaded: one compact line ---------------- */
  if (accountsScope.kind !== 'none' && !expanded) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-white border border-slate-200 rounded-2xl text-xs shadow-xs">
        <div className="flex items-center gap-2 min-w-0">
          <Database className="w-4 h-4 text-blue-600 shrink-0" />
          <span className="text-slate-500">Showing</span>
          <span className="font-bold text-slate-900 truncate">
            {accountsScope.kind === 'all' ? 'every account' : accountsScope.label}
          </span>
          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full font-bold">
            {accounts.length.toLocaleString('en-IN')} loaded
          </span>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold transition"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          <span>Change selection</span>
        </button>
      </div>
    );
  }

  /* ---------------- nothing loaded: the gate ---------------- */
  return (
    <div className="p-4 bg-white border border-slate-200 rounded-2xl space-y-3 text-xs shadow-xs">
      <div className="flex items-start gap-2">
        <Search className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
        <div>
          <h3 className="font-bold text-sm text-slate-900">
            Choose what to open
          </h3>
          <p className="text-slate-500 mt-0.5 leading-relaxed">
            Nothing is read from the database until you choose. Opening Accounts
            used to read every account in the book every time, for every person
            signed in — which is what the bill was for.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="block text-slate-700 font-bold mb-1">Bank</label>
          <select
            value={bank}
            onChange={(e) => {
              setBank(e.target.value);
              setZone('');
              setBranch('');
              setAgentId('');
            }}
            className={fieldCls}
          >
            <option value="">Select bank…</option>
            {bankOptions.map((b) => (
              <option key={`sb-bank-${b}`} value={b}>
                {b}
                {counts[`bank:${b}`] >= 0 ? ` (${counts[`bank:${b}`]})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-slate-700 font-bold mb-1">Zone</label>
          <select
            value={zone}
            disabled={!bank}
            onChange={(e) => {
              setZone(e.target.value);
              setBranch('');
              setAgentId('');
            }}
            className={fieldCls}
          >
            <option value="">{bank ? 'All zones' : 'Pick a bank first'}</option>
            {zoneOptions.map((z) => (
              <option key={`sb-zone-${z}`} value={z}>
                {z}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-slate-700 font-bold mb-1">Branch</label>
          <select
            value={branch}
            disabled={!bank}
            onChange={(e) => {
              setBranch(e.target.value);
              setAgentId('');
            }}
            className={fieldCls}
          >
            <option value="">{bank ? 'All branches' : 'Pick a bank first'}</option>
            {branchOptions.map((b) => (
              <option key={`sb-br-${b}`} value={b}>
                {b}
                {counts[`branch:${b}`] >= 0 ? ` — ${counts[`branch:${b}`]} accounts` : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-slate-700 font-bold mb-1">
            Agent <span className="font-medium text-slate-400">(narrowest)</span>
          </label>
          <select
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            className={fieldCls}
          >
            <option value="">Any agent</option>
            {agentOptions.map((u) => (
              <option key={`sb-ag-${u.id}`} value={u.agentId || u.id}>
                {u.name} ({u.agentId || u.id})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button
          type="button"
          onClick={doLoad}
          disabled={!canLoad || accountsLoading}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-xl font-bold shadow-sm transition"
        >
          {accountsLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Search className="w-4 h-4" />
          )}
          <span>{accountsLoading ? 'Reading…' : 'Open these accounts'}</span>
        </button>

        <button
          type="button"
          onClick={doCount}
          disabled={!canLoad || counting}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl font-bold transition disabled:opacity-50"
          title="One read per count, however many accounts it counts"
        >
          {counting ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Users className="w-3.5 h-3.5" />
          )}
          <span>How many are there?</span>
        </button>

        <div className="flex-1" />

        {/* The expensive one, deliberately made to look like what it is.
            
            It used to sit here as a solid amber button the same size as the
            others, on the right where a primary action normally goes -- so it
            was easy to press instead of "Open these accounts", and then the
            warning looked like it had appeared for no reason. It is now plain
            text, and the confirmation names the scope it is about to ignore. */}
        <button
          type="button"
          onClick={async () => {
            const chosen = [
              bank && `bank ${bank}`,
              zone && `zone ${zone}`,
              branch && `branch ${branch}`,
              agentId && 'the chosen agent',
            ]
              .filter(Boolean)
              .join(', ');

            const warning = chosen
              ? `You have chosen ${chosen}.\n\n` +
                'THIS IGNORES THAT and reads EVERY account in the database — ' +
                'at 80,000 accounts that is 80,000 reads.\n\n' +
                'It is only needed for Allocation and Bulk Delete. To open the ' +
                'accounts you selected, use "Open these accounts" instead.\n\n' +
                'Read everything anyway?'
              : 'This reads EVERY account in the database into memory.\n\n' +
                'It is what Allocation and Bulk Delete need, and it is the ' +
                'expensive one — at 80,000 accounts it is 80,000 reads.\n\n' +
                'Continue?';

            if (!window.confirm(warning)) return;
            const res = await loadAllAccounts();
            setMessage(res.message);
            if (res.success) setExpanded(false);
          }}
          disabled={accountsLoading}
          className="text-[11px] font-semibold text-slate-500 hover:text-amber-700 underline underline-offset-2 disabled:opacity-50"
          title="Only for Allocation and Bulk Delete — reads the whole database"
        >
          Load every account (Allocation / Bulk Delete only)
        </button>
      </div>

      {message && (
        <p className="text-[11px] text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          {message}
        </p>
      )}

      {!canLoad && (
        <p className="text-[11px] text-amber-700 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          <span>Choose at least a bank to continue.</span>
        </p>
      )}
    </div>
  );
};

export default AccountsScopeBar;
